// =============================================================================
// CONNECT 2.0 — Dashboard work map scoping + urgency (Spec 10, Task 3).
// -----------------------------------------------------------------------------
// Pure helpers over the same flat `/project/user-projects` shape
// (`Project { sites: SiteDetail[] }`, `SiteDetail { site, proposal }`) that the
// list pages consume. They turn that feed into the physical-site rows the work
// map plots, applying the SAME per-scope status filters the corresponding list
// pages already apply so the map never invents its own status rules.
//
// The scopes select sites differently:
//   • My Offers  → opportunities extended to the vendor (invited-to-quote +
//                  offered-to-accept), keyed off the per-site proposal status.
//   • My Active  → active-page.component.ts's fetchData() filter (the `/active`
//                  list — post-award, in-progress work).
//   • Available Work → a separate discovery feed (`AvailableWorkService`, i.e.
//                  connect-service's `GET /staffing-need`), NOT the `[projects]`
//                  input — see the Available Work section at the foot of this file.
//
// No Angular dependency, so the scoping/urgency rules stay unit-testable on
// their own and the component stays a thin render shell. That also means no
// service is ever injected/imported here: anything needing Angular (address
// formatting via `CommonUtilityServiceService`, palette lookups) is done by the
// component, which composes those with the pure helpers below.
// =============================================================================

import {
  sanitizeProjectDetails,
  toProjectVms,
} from '../../list/list-grouping.util';
import { AvailableWork } from '../../model/available-work';
import { haversineMiles, LatLng } from '../../shared/haversine/haversine.util';
import { ORDER_TYPE_TITLES } from '../../shared/order-type-titles/order-type-titles';

/**
 * Work-map scopes. `offers`/`active` are driven by the `[projects]` input;
 * `availableWork` is driven by the separate Available Work feed
 * (`AvailableWorkService`). All three share one row view-model
 * (`WorkMapRowView`) and therefore one marker / row / legend path.
 */
export type WorkMapScope = 'offers' | 'active' | 'availableWork';

/** Urgency bucket derived from a site's nearest product due date. */
export type WorkMapUrgency = 'overdue' | 'soon' | 'onTrack' | 'none';

// Per-site proposal statuses the My Offers scope keeps: opportunities actively
// extended to the vendor — invited-to-quote (`INVITATION`) + offered-to-accept
// (`OFFERED`) + countered-and-awaiting-staffing (`COUNTERED`, Task B4 — still
// unresolved, so still an open opportunity) + re-offered-after-a-counter
// (`RE_OFFERED` — staffing came back with a revised fee, so the PA has a live
// offer to answer). This deliberately NO LONGER
// mirrors proposals-page.component.ts's `/proposals` filter (submitted/lost/
// awarded); the work map's My Offers is an "opportunity" scope, not a
// submitted-offers list.
const MY_OFFERS_STATUSES = new Set<string>([
  'INVITATION',
  'OFFERED',
  'COUNTERED',
  'RE_OFFERED',
]);

// Per-site proposal statuses active-page.component.ts's fetchData() keeps for
// the `/active` (My Active) list — copied verbatim from that page's filter.
const MY_ACTIVE_STATUSES = new Set<string>([
  'ACTIVE',
  'REVIEW_PENDING',
  'REVISION_REQUESTED',
  'REVISION_PENDING',
]);

// A site is "due soon" when its nearest product due date is within this many
// days of now (and not yet past). One neat threshold keeps the legend to three
// urgency buckets + a neutral "no date" fallback.
export const URGENCY_SOON_DAYS = 7;

/** One physical-site row the work map plots + lists. */
export interface WorkMapRow {
  /** Stable, cross-project-unique row key (`<projectId>:<siteVm.key>`). */
  key: string;
  /** Physical site number, or null when the source omitted it. */
  siteNumber: number | null;
  /** Owning project id (for deep-links / context). */
  projectId: string;
  /** Representative raw site object (address/parcels live here). */
  site: any;
  /** Products (service line-items) at this site, from the shared grouping VM. */
  products: any[];
  /** Marker point, or null when the site carries no usable parcel centroid. */
  position: { lat: number; lng: number } | null;
  /** Nearest (earliest) product due date across the site, or null. */
  dueDate: Date | null;
  /** Urgency bucket derived from `dueDate`. */
  urgency: WorkMapUrgency;
  /** Owning project's rush flag — drives the My Offers rush-based status. */
  rush: boolean;
}

/**
 * Apply a scope's status filter to the raw project feed, mirroring the matching
 * list page's own `fetchData()` filter EXACTLY (see the status sets above).
 * Returns flat, page-filtered projects ready for `toProjectVms`.
 */
export function filterProjectsForScope(
  projects: any[],
  scope: WorkMapScope
): any[] {
  const sanitized = sanitizeProjectDetails(projects);

  if (scope === 'active') {
    // Mirrors active-page.component.ts fetchData().
    return sanitized
      .map((parent: any) => ({
        ...parent,
        sites: parent.sites.filter((child: any) =>
          MY_ACTIVE_STATUSES.has(child?.proposal?.status)
        ),
      }))
      .filter((parent: any) => parent.sites.length > 0 && parent.projectId);
  }

  // My Offers — sites whose proposal is an open opportunity to the vendor
  // (INVITATION / OFFERED). No lost/no-response guard is needed: those statuses
  // can never survive `MY_OFFERS_STATUSES`.
  return sanitized
    .map((parent: any) => ({
      ...parent,
      sites: parent.sites.filter((child: any) =>
        MY_OFFERS_STATUSES.has(child?.proposal?.status)
      ),
    }))
    .filter((parent: any) => parent.sites.length > 0 && parent.projectId);
}

/**
 * Marker point for a site. Reuses the SAME parcel centroid the app already
 * plots with — `CommonUtilityServiceService.determineMapCenter` reads
 * `parcels[0].center` as `[lat, lng]` — but returns null (rather than that
 * helper's continental-US fallback) when no centroid exists, so a
 * coordinate-less site simply gets no marker instead of a misleading pin in
 * Kansas. Its list row still renders and stays fully functional.
 */
export function siteCoordinate(site: any): { lat: number; lng: number } | null {
  const center = site?.parcels?.[0]?.center;
  if (
    Array.isArray(center) &&
    center.length === 2 &&
    Number.isFinite(center[0]) &&
    Number.isFinite(center[1])
  ) {
    return { lat: center[0], lng: center[1] };
  }
  return null;
}

/**
 * A product's due date, mirroring product-line.component.ts's
 * `completedDueDate` (`proposedDeliverDate` falling back to
 * `deliverableDueDate`) — the same field the list surfaces as "Due Date".
 */
export function proposalDueDate(proposal: any): Date | null {
  const raw = proposal?.proposedDeliverDate ?? proposal?.deliverableDueDate ?? null;
  if (!raw) {
    return null;
  }
  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

/** Earliest (most urgent) product due date across a grouped site, or null. */
function earliestDueDate(products: any[]): Date | null {
  let earliest: Date | null = null;
  for (const product of products ?? []) {
    const due = proposalDueDate(product?.proposal);
    if (due && (!earliest || due.getTime() < earliest.getTime())) {
      earliest = due;
    }
  }
  return earliest;
}

/** Bucket a due date into an urgency level relative to `now` (epoch ms). */
export function urgencyFor(dueDate: Date | null, now: number): WorkMapUrgency {
  if (!dueDate) {
    return 'none';
  }
  const deltaMs = dueDate.getTime() - now;
  if (deltaMs < 0) {
    return 'overdue';
  }
  if (deltaMs <= URGENCY_SOON_DAYS * 24 * 60 * 60 * 1000) {
    return 'soon';
  }
  return 'onTrack';
}

/**
 * Resolve the router commands for a row's "View project" navigation
 * (Change 2 — marker InfoWindow). Scope-dependent:
 *  - `availableWork` → **no route at all.** A staffing need has no project, no
 *    proposal and no workspace, so there is no destination to offer — and the
 *    pin click has already scrolled the paired card (which carries Express
 *    Interest) into view, so the vendor reaches the action without one. The
 *    template must not render the button for this scope either, and NOT render
 *    it disabled: a disabled control implies somewhere to go once some
 *    condition is met, and there never is. This branch is what makes that a
 *    property of the route resolver rather than a template-only omission.
 *  - `active` → the workspace route `/active/:id`, keyed on the same
 *    Active-phase product's site id `site-card.component.ts`'s
 *    `openWorkspaceSiteId` uses — found the same way (first product whose
 *    status is Active-phase). In this scope every product in the row already
 *    passed `MY_ACTIVE_STATUSES` upstream (see `filterProjectsForScope`), so
 *    this is guaranteed to find one; the lookup is kept for parity with that
 *    pattern rather than just trusting `row.products[0]`.
 *  - `offers` → the Invitations list (`/invitations`) — My Offers is the
 *    INVITATION + OFFERED set, which renders on the Invitations page (its
 *    filter is exactly INVITATION||OFFERED), NOT `/proposals`; offers have no
 *    per-site workspace to deep-link into.
 * Returns null for `availableWork`, and for an `active` row that somehow
 * carries no Active-phase product (shouldn't happen given the scope filter).
 */
export function workMapProjectRoute(
  row: WorkMapRow,
  scope: WorkMapScope
): any[] | null {
  if (scope === 'availableWork') {
    return null;
  }
  if (scope === 'active') {
    const activeProduct = row.products.find((product: any) =>
      MY_ACTIVE_STATUSES.has(product?.status)
    );
    const siteId = activeProduct?.site?.id ?? null;
    return siteId ? ['/active', siteId] : null;
  }
  return ['/invitations'];
}

/**
 * Build the work-map rows for a scope: page-filter the feed, group into
 * physical-site VMs (reusing `toProjectVms`), then decorate each site with its
 * marker point + urgency. One row per physical site, insertion order preserved.
 */
export function buildWorkMapRows(
  projects: any[],
  scope: WorkMapScope,
  now: number = Date.now()
): WorkMapRow[] {
  const vms = toProjectVms(filterProjectsForScope(projects, scope));
  const rows: WorkMapRow[] = [];
  for (const vm of vms) {
    for (const siteVm of vm.sites) {
      const dueDate = earliestDueDate(siteVm.products);
      rows.push({
        key: `${vm.projectId}:${siteVm.key}`,
        siteNumber: siteVm.siteNumber,
        projectId: vm.projectId,
        site: siteVm.site,
        products: siteVm.products,
        position: siteCoordinate(siteVm.site),
        dueDate,
        urgency: urgencyFor(dueDate, now),
        rush: !!vm.rush,
      });
    }
  }
  return rows;
}

// =============================================================================
// Shared work-type / rush-status axes.
// -----------------------------------------------------------------------------
// Two small string-union types the rush-status + work-type helpers
// (`workMapRowStatus` / `workMapRowWorkType` below) and their component
// consumers (legend metadata, row view-model) read. Scope-neutral names, and
// deliberately so: `WorkMapStatus` is SHARED by My Offers and Available Work —
// both are opportunity scopes driven by the same `rush` flag, so they share one
// axis (one palette, one legend shape, one Critical filter) with per-scope
// labels. `WorkMapWorkType` serves the project scopes only; a staffing need is
// neither a Quote Request nor a Work Assignment.
// =============================================================================

/** Work type — fixed-price Work Assignment vs open Quote Request (project scopes only). */
export type WorkMapWorkType = 'workAssignment' | 'quoteRequest';

/** Rush-based status — `critical` when the owning work is rush, else `needsStaffing`. */
export type WorkMapStatus = 'needsStaffing' | 'critical';

// =============================================================================
// Project-scope filters (My Offers / My Active) — pure helpers over WorkMapRow.
// -----------------------------------------------------------------------------
// The two project scopes filter the SAME `WorkMapRow[]` `buildWorkMapRows`
// produces, on two axes:
//   • Report type — derived from the row's PRODUCTS. A physical-site row can
//     hold several products/services, so a row exposes a SET of report types and
//     MATCHES a selected type when ANY product matches. Per product the display
//     value is the sub-type when present else the service type
//     (`siteSubType || siteType`).
//   • Due date — bucketed straight off the row's precomputed `urgency`, so the
//     map never re-derives due dates: Past Due = `overdue`; Upcoming = a future
//     due date (`soon`/`onTrack`); All = every row (incl. no-due-date).
// =============================================================================

/** Report-type display value for one product (sub-type when present, else type). */
export function productReportType(product: any): string {
  const site = product?.site ?? {};
  return (site.siteSubType || site.siteType || '').toString().trim();
}

/** Distinct report-type display values present across a single row's products. */
export function rowReportTypes(row: WorkMapRow): string[] {
  const seen = new Set<string>();
  for (const product of row?.products ?? []) {
    const report = productReportType(product);
    if (report) {
      seen.add(report);
    }
  }
  return Array.from(seen);
}

/** Distinct report-type display values present across project rows, sorted. */
export function projectReportTypeOptions(rows: WorkMapRow[]): string[] {
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    for (const report of rowReportTypes(row)) {
      seen.add(report);
    }
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

/** Due-date filter bucket for the project scopes. */
export type WorkMapDueDate = 'all' | 'upcoming' | 'pastDue';

/**
 * True when a row falls in a due-date bucket, reading only its `urgency`:
 *   • `pastDue`  → `overdue`.
 *   • `upcoming` → a future due date (`soon`/`onTrack`), excludes no-date rows.
 *   • `all`      → every row (including `none`/no due date).
 */
export function matchesDueDate(row: WorkMapRow, bucket: WorkMapDueDate): boolean {
  if (bucket === 'all') {
    return true;
  }
  if (bucket === 'pastDue') {
    return row.urgency === 'overdue';
  }
  return row.urgency === 'soon' || row.urgency === 'onTrack';
}

/** Live due-date bucket counts over a full (unfiltered) row set. */
export function projectDueDateCounts(
  rows: WorkMapRow[]
): { all: number; upcoming: number; pastDue: number } {
  let upcoming = 0;
  let pastDue = 0;
  for (const row of rows ?? []) {
    if (row.urgency === 'overdue') {
      pastDue++;
    } else if (row.urgency === 'soon' || row.urgency === 'onTrack') {
      upcoming++;
    }
  }
  return { all: (rows ?? []).length, upcoming, pastDue };
}

// -----------------------------------------------------------------------------
// My Offers rush-based status (marker/legend coloring only). My Offers is an
// "opportunity" scope, so its markers stay rush-driven: `critical` when the
// owning project is rush, else the neutral non-critical bucket. The FILTER,
// however, is work-type + Critical (see below), not a status axis, so
// `workMapRowStatus` now feeds coloring alone.
// -----------------------------------------------------------------------------

/**
 * `Critical` when the row's owning work is rush, else the neutral bucket.
 *
 * The parameter is the structural `{ rush }` subset rather than a full
 * `WorkMapRow` so BOTH row sources feed this one axis: a project row (whose
 * `rush` is the owning project's flag) and an Available Work need (whose
 * `rush` is order-service's enrichment of the same bulk-load flag). Widening
 * the parameter is backward compatible — every existing `WorkMapRow` caller
 * still type-checks — and it is what lets the two scopes share one status
 * palette, one legend and one Critical filter instead of growing a parallel
 * axis.
 *
 * A null/absent `rush` (a need stored before the field existed) lands in the
 * neutral bucket. That is NOT this function claiming the work isn't urgent —
 * `StaffingNeed.rush` is boxed precisely because null means "the producer did
 * not say", which is a different claim from false, and it must not be rendered
 * as "not rush". The neutral bucket makes no urgency assertion either way: it
 * is the absence of the Critical highlight, not a "Standard"/"not rush" label,
 * which is why an unknown `rush` can sit in it without stating anything untrue.
 * Only `rush === true` is ever asserted on screen.
 */
export function workMapRowStatus(row: { rush?: boolean | null } | null | undefined): WorkMapStatus {
  return row?.rush ? 'critical' : 'needsStaffing';
}

// -----------------------------------------------------------------------------
// My Offers work-type. Mirrors the Invitations page distinction: a row is a
// Work Assignment when it's fixed-price, else a Quote Request. Fixed-price is
// read PREFERABLY off the site's `fixedBidAmount` marker (a `!= null` check);
// when that field isn't present on the project feed's site object, it falls
// back to "any product's `proposal.fixedAmount` is truthy" — the exact rule
// `product-line.component.ts` uses (`!!proposal.fixedAmount`).
// -----------------------------------------------------------------------------

/** True when a My Offers row is fixed-price (⇒ Work Assignment). */
export function workMapRowIsFixedPrice(row: WorkMapRow): boolean {
  if (row?.site?.fixedBidAmount != null) {
    return true;
  }
  return (row?.products ?? []).some((product: any) => !!product?.proposal?.fixedAmount);
}

/** Work type of a My Offers row — fixed-price Work Assignment vs open Quote Request. */
export function workMapRowWorkType(row: WorkMapRow): WorkMapWorkType {
  return workMapRowIsFixedPrice(row) ? 'workAssignment' : 'quoteRequest';
}

/**
 * The My Offers filter facet. Single-select over the full in-scope set; the
 * underlying subsets may overlap (a Critical row can also be a Quote Request /
 * Work Assignment — that's expected):
 *   • `quoteRequest`   → non-fixed-price rows.
 *   • `workAssignment` → fixed-price rows.
 *   • `critical`       → rush rows.
 *   • `all`            → every row.
 */
export type OffersFacet = 'all' | 'quoteRequest' | 'workAssignment' | 'critical';

/**
 * The two axes an `OffersFacet` reads: `isFixedPrice` ⇒ Work Assignment (else
 * Quote Request), `rush` ⇒ Critical. A My Offers row maps itself into this
 * shape via `rowWorkTypeInput`, keeping one matcher + counter for the facet.
 */
export interface WorkTypeFacetInput {
  isFixedPrice: boolean;
  rush: boolean;
}

/** True when a normalized (fixed-price, rush) pair matches an `OffersFacet` selection. */
export function matchesWorkTypeFacet(
  input: WorkTypeFacetInput,
  facet: OffersFacet
): boolean {
  switch (facet) {
    case 'quoteRequest':
      return !input.isFixedPrice;
    case 'workAssignment':
      return input.isFixedPrice;
    case 'critical':
      return input.rush === true;
    default:
      return true; // 'all'
  }
}

/** Live facet counts over a full (unfiltered) input set (subsets overlap). */
export function workTypeFacetCounts(
  inputs: WorkTypeFacetInput[]
): { all: number; quoteRequest: number; workAssignment: number; critical: number } {
  let quoteRequest = 0;
  let workAssignment = 0;
  let critical = 0;
  for (const input of inputs ?? []) {
    if (input.isFixedPrice) {
      workAssignment++;
    } else {
      quoteRequest++;
    }
    if (input.rush) {
      critical++;
    }
  }
  return { all: (inputs ?? []).length, quoteRequest, workAssignment, critical };
}

/** Normalize a My Offers project row into the shared work-type/Critical facet axes. */
export function rowWorkTypeInput(row: WorkMapRow): WorkTypeFacetInput {
  return { isFixedPrice: workMapRowIsFixedPrice(row), rush: !!row.rush };
}

/** True when a My Offers row matches an `OffersFacet` selection. */
export function matchesOffersFacet(row: WorkMapRow, facet: OffersFacet): boolean {
  return matchesWorkTypeFacet(rowWorkTypeInput(row), facet);
}

/** Live My Offers facet counts over a full (unfiltered) row set (subsets overlap). */
export function offersFacetCounts(
  rows: WorkMapRow[]
): { all: number; quoteRequest: number; workAssignment: number; critical: number } {
  return workTypeFacetCounts((rows ?? []).map(rowWorkTypeInput));
}

/**
 * The project-scope filter selection — `'all'` sentinels mean "no constraint".
 * `reportType` is shared by both project scopes; the primary axis is per-scope:
 * `dueDate` drives My Active, `offers` (work-type + Critical) drives My Offers.
 */
export interface ProjectRowFilter {
  reportType: string; // 'all' or a report-type display value
  dueDate: WorkMapDueDate; // My Active only
  offers: OffersFacet; // My Offers only
}

/**
 * True when a project row passes the report-type filter AND its scope's primary
 * axis: My Active filters on the due-date bucket, My Offers on the work-type /
 * Critical facet.
 */
export function projectRowMatchesFilter(
  row: WorkMapRow,
  filter: ProjectRowFilter,
  scope: WorkMapScope
): boolean {
  if (filter.reportType !== 'all' && !rowReportTypes(row).includes(filter.reportType)) {
    return false;
  }
  return scope === 'active'
    ? matchesDueDate(row, filter.dueDate)
    : matchesOffersFacet(row, filter.offers);
}

/** Apply the project-scope filter to a row set, preserving order. */
export function filterProjectRows(
  rows: WorkMapRow[],
  filter: ProjectRowFilter,
  scope: WorkMapScope
): WorkMapRow[] {
  return (rows ?? []).filter((row) => projectRowMatchesFilter(row, filter, scope));
}

// =============================================================================
// Row view-model — a DISCRIMINATED UNION over the two data sources.
// -----------------------------------------------------------------------------
// The component renders ONE marker/row/legend path. Everything that path needs
// lives on `RowViewBase`; the `kind` discriminator is consulted only where the
// two sources genuinely differ — the card body and the action area. Keeping the
// union here (rather than in the component) is what makes the field-level
// mapping unit-testable, and keeps `position` a plain `{ lat, lng }` instead of
// dragging the ambient `google.maps` namespace into this Angular-free file.
// =============================================================================

/** Fields every row view exposes, so the marker/render path stays generic. */
export interface RowViewBase {
  /** Stable, list-unique row key. */
  key: string;
  /** Marker point, or null when the row has no usable coordinate (row still lists). */
  position: { lat: number; lng: number } | null;
  /** Marker/dot color. */
  color: string;
  /** Short row title (`#<siteNumber>` / property name, or a fallback). */
  title: string;
  /** Formatted street address (top line). */
  streetAddress: string;
  /** Formatted city/state/zip (sub line). */
  cityStateZip: string;
}

/** A row backed by the vendor's own project feed (My Offers / My Active). */
export interface ProjectRowView extends RowViewBase {
  kind: 'project';
  row: WorkMapRow;
  /** Work type (My Offers cards surface this as a badge). */
  workType: WorkMapWorkType;
  /** Work-type badge label — "Work Assignment" / "Quote Request". */
  workTypeLabel: string;
}

/**
 * A row backed by the Available Work discovery feed (`GET /staffing-need`).
 *
 * `nearestNeedMiles` is `NaN` — never 0 — when the distance is unknowable (the
 * need has no `geoPoint`, or there is no OTHER need in the reference set to
 * measure against). See `availableWorkProximity` /
 * `compareAvailableWorkProximity`.
 */
export interface AvailableWorkRowView extends RowViewBase {
  kind: 'availableWork';
  /** The raw feed row — the card body and Task 8's actions read it. */
  need: AvailableWork;
  /** Rush-driven status, via the shared `workMapRowStatus` axis. */
  status: WorkMapStatus;
  /** Status label beside the dot ("Needs staffing" / "Critical"). */
  statusLabel: string;
  /** Report-type badge value (sub-type when present, else service type). */
  reportType: string;
  /** One muted line of building area / acreage / year built; `''` when all are null. */
  metadataLine: string;
  /** Miles to the NEAREST OTHER need in the reference set, or `NaN` when unknowable. */
  nearestNeedMiles: number;
  /** True only for a finite distance within `MILK_RUN_MILES`. */
  isMilkRun: boolean;
  /** Title of that nearest other need, or `null` when there is none. */
  closestNeedName: string | null;
  /** `nearestNeedMiles` rounded to whole miles, or `null` when there is none. */
  closestNeedMiles: number | null;
  /**
   * Distance as the BADGE renders it — `"<1 mi"` under half a mile, else
   * `"<n> mi"`; `''` when there is no neighbour. Distinct from
   * `closestNeedMiles`, which stays a number for anything that needs to compute
   * with it; `Math.round` alone renders a sub-mile neighbour as "0 mi", which
   * reads as a broken calculation. See `formatMilkRunMiles`.
   */
  closestNeedDistanceLabel: string;
  /**
   * Hover text for the milk-run badge — "Bundle with <name> · <n> mi away", or
   * `''` when there is no neighbour. Assembled at row-build time (like
   * `rowTestIds`) rather than by a template-called method, so change detection
   * doesn't re-derive a string per row per cycle.
   */
  milkRunTooltip: string;
}

/**
 * Row view-model the template + render path consume, tagged by `kind` so a need
 * row and a project row share one marker/row/legend path.
 */
export type WorkMapRowView = ProjectRowView | AvailableWorkRowView;

// =============================================================================
// Available Work scope — pure helpers over the `GET /staffing-need` feed.
// -----------------------------------------------------------------------------
// Every one of the four enrichment fields (`rush`, `totalBuildingSqft`,
// `totalAcreage`, `yearBuilt`) is nullable: a need stored before those fields
// existed carries null, and the full-snapshot push contract means an omitted
// field UNSETS the stored value. So each is presence-checked individually
// rather than treated as a group — a need with one of the three property facts
// still shows that one.
// =============================================================================

/** Milk-run threshold: badge a need within this many miles of the nearest OTHER open need. */
export const MILK_RUN_MILES = 50;

/**
 * True when the vendor holds at least one ENABLED service, i.e. qualifies for
 * something at all.
 *
 * <b>Why this exists client-side.</b> The two empty feeds are indistinguishable
 * from the response: "no enabled services" and "enabled services, nothing
 * matching" are BOTH an empty array inside a `SUCCESS` envelope with an empty
 * message. connect-service separates them only in its logs
 * (`reason=no_enabled_services` vs `reason=no_open_needs_for_qualified_services`
 * — see `StaffingNeedHandler.getAvailableWork`). They need different answers on
 * screen, though: the first is the one actionable case (send the vendor to
 * their services), the second is a genuine empty. So this mirrors the backend's
 * `enabledServiceTypes` rule against the same field on the cached user rather
 * than inventing a response field.
 *
 * <b>The rule, and the three ways to get it wrong.</b> Mirrored predicate for
 * predicate from `StaffingNeedHandler.enabledServiceTypes`:
 *  - `status === 'ENABLED'` and **nothing else** — never the `enabled` /
 *    `enabledForZone` booleans, and never branching on `statusSource`. Reading
 *    `enabledForZone` instead silently inverts the whole distinction.
 *  - **Zone-blind** — enabled in ANY zone counts, because eligibility is
 *    skillset-only and a need's geography is never a gate.
 *  - a non-blank `type`, since a type-less entry qualifies for nothing.
 * `status` arrives as the enum CONSTANT NAME (`UserServiceStatusEnum`, e.g.
 * `"ENABLED"` / `"PENDING"`), so this compares strings.
 *
 * <b>Staleness, and which way it fails.</b> The input is the cached user
 * (`getStoredUser()`), which `AuthenticationService.me()` REPLACES with the
 * fresh profile on navigation — so the window is small. Since it reads the same
 * document field the backend reads, a divergence can only come from that cache.
 * If it is wrong in the "none" direction, the vendor is routed to their
 * services page, which shows the real statuses — a benign destination, not a
 * false claim.
 *
 * Typed loosely (`serviceZones` is `any` on the `User` model) and kept pure so
 * this file stays Angular-free: the caller passes the user in.
 */
export function hasEnabledServiceTypes(
  user: { serviceZones?: any } | null | undefined
): boolean {
  const zones = user?.serviceZones;
  if (!Array.isArray(zones)) {
    return false;
  }
  return zones.some((zone: any) =>
    (Array.isArray(zone?.services) ? zone.services : []).some(
      (service: any) =>
        service?.status === 'ENABLED' &&
        typeof service?.type === 'string' &&
        service.type.trim() !== ''
    )
  );
}

/**
 * Marker point for a need. connect-service serializes `geoPoint.coordinates` in
 * GeoJSON order — `[lng, lat]` — so this swaps them; getting that backwards
 * plots the whole feed in the wrong hemisphere. Returns null for an absent or
 * non-finite coordinate pair, which is a NORMAL outcome of the push contract
 * (see `AvailableWorkLocation`): the row still lists and still counts, it just
 * plots no pin.
 */
export function availableWorkPosition(
  need: AvailableWork | null | undefined
): { lat: number; lng: number } | null {
  const coordinates = need?.location?.geoPoint?.coordinates;
  if (
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    Number.isFinite(coordinates[0]) &&
    Number.isFinite(coordinates[1])
  ) {
    return { lat: coordinates[1], lng: coordinates[0] };
  }
  return null;
}

/**
 * Shape a need's `location` into the `{ site: { parcels: [...] } }` argument
 * `CommonUtilityServiceService.formatAddress` expects, so Available Work
 * addresses go through the SAME formatter every other screen uses (Title-Cased
 * street, `city, State, zip`) instead of a second, subtly different one.
 *
 * Two consequences worth knowing, both inherited on purpose:
 *  - `location.state` arrives as the enum CONSTANT NAME (`"CALIFORNIA"`), not
 *    an abbreviation — connect-service's `StateEnum` carries no `@JsonValue` —
 *    which is exactly what that formatter's `decodeUsState` map is keyed on, so
 *    it renders the full state name ("California"), the app-wide convention.
 *  - `parcels` is always present (a one-element array), because `formatAddress`
 *    dereferences `data.site.parcels` and would throw on a bare object.
 */
export function availableWorkAddressSource(need: AvailableWork | null | undefined): any {
  const location = need?.location ?? {};
  return {
    site: {
      parcels: [
        {
          street: location.street ?? '',
          city: location.city ?? '',
          state: location.state ?? '',
          zipcode: location.zipcode ?? '',
        },
      ],
    },
  };
}

/**
 * Identity of the PROPERTY a need sits on, used to keep two needs on the same
 * property from being sold to each other as a milk run.
 *
 * <b>Why an address and not the coordinates.</b> Co-located needs are exactly
 * what has to be recognised here, and the coordinate pair cannot tell "one
 * property, two scopes" apart from "two properties that happen to share a
 * point" — see the note on `availableWorkProximity`'s self-exclusion. The
 * address is the thing "a different property" actually means, and both needs
 * of a dual-scope order carry a byte-identical one: order-service fills a
 * need's street/city/zipcode from the same project's first site's first
 * location for every product on it.
 *
 * <b>Why not the order number.</b> `externalOrderNumber` looks like the more
 * precise key (`2608-A2674-001-ESA` and `…-001-PCA` share a project-and-site
 * trunk), and it is not usable: order-service only emits that shape when its
 * `NEW_PORTFOLIO_ID_COUNTER` feature is effective and otherwise falls back to
 * an encoded date with no structure at all, so the trunk is absent in exactly
 * the environments where nothing would reveal it. The portfolio id also
 * carries its own hyphen (`2608-A2674`), leaving no stable segment count to
 * split on.
 *
 * Returns `''` when there is no address to key on, which callers must treat as
 * "no identity" rather than as an identity every address-less need shares —
 * the same rule `siteGroupKey` applies when neither of its keys is present.
 * `zipcode` is part of the key because street+city alone collides across
 * metros.
 */
export function availableWorkPropertyKey(need: AvailableWork | null | undefined): string {
  const parts = [need?.location?.street, need?.location?.city, need?.location?.zipcode]
    .map((part) => (part ?? '').toString().trim().replace(/\s+/g, ' ').toLowerCase())
    .filter((part) => part !== '');
  return parts.length > 0 ? parts.join('|') : '';
}

/**
 * Report-type display value for a need — sub-type when present, else service
 * type, DECODED to its human title (`PHASE_1` → "Phase I (ESA)").
 *
 * The decode happens here, at the single source, rather than in a separate
 * label field beside the raw value. Three consumers read `reportType` — the
 * card badge, `availableWorkReportTypeOptions` (which seeds the filter
 * dropdown) and `matchesAvailableWorkReportType` (which also narrows the
 * milk-run reference set). Decoding at the source moves all three together; a
 * label field alongside the raw value would leave the badge reading "Phase I
 * (ESA)" while the dropdown still offered `PHASE_1`, and the two would compare
 * unequal.
 *
 * Unknown values fall back to the RAW enum, never to a placeholder. This
 * function prefers `serviceSubType`, and sub-types are not order-type enum keys
 * (the only one connect-service emits today is order-service's
 * `SITE_VISIT_ONLY_SUB_TYPE`), so an un-decoded value is a normal outcome here
 * — showing the raw string is worse-looking than a title but strictly more
 * informative than an em dash, and it is still a usable filter value.
 *
 * `ORDER_TYPE_TITLES` is a static build-time map, deliberately not the synced
 * Order Type Catalog the internal Manage Service Types screen prefers: that
 * catalog is read over an internal-scoped endpoint, and this is a vendor
 * surface.
 */
export function availableWorkReportType(need: AvailableWork | null | undefined): string {
  const raw = (need?.serviceSubType || need?.serviceType || '').toString().trim();
  return ORDER_TYPE_TITLES[raw] || raw;
}

/**
 * The card's ONE muted metadata line: building area / acreage / year built.
 *
 * All three are independently nullable, so each is checked on its own and only
 * the present ones are joined — a need missing acreage renders the other two
 * with no stray separator, and a need missing all three returns `''` so the
 * template can drop the whole element rather than render an empty line. Units
 * match `site-card.component.ts`'s existing vocabulary ("Sq. Ft." / "Acres")
 * so the same facts read identically on both surfaces. A year is NOT
 * thousands-separated (1998, not 1,998).
 */
export function availableWorkMetadataLine(need: AvailableWork | null | undefined): string {
  const parts: string[] = [];
  if (Number.isFinite(need?.totalBuildingSqft)) {
    parts.push(`${(need!.totalBuildingSqft as number).toLocaleString('en-US')} Sq. Ft.`);
  }
  if (Number.isFinite(need?.totalAcreage)) {
    parts.push(`${(need!.totalAcreage as number).toLocaleString('en-US')} Acres`);
  }
  if (Number.isFinite(need?.yearBuilt)) {
    parts.push(`Built ${need!.yearBuilt}`);
  }
  return parts.join(' · ');
}

/**
 * The fields the proximity computation reads off a candidate need, and the TWO
 * identities it excludes by.
 *
 * <b>`key` (the need id)</b> excludes the row itself — NOT its array position:
 * two needs can sit on the same parcel centroid, so matching on coordinates or
 * index would let a need be its own neighbour.
 *
 * <b>`propertyKey` (the address)</b> excludes every OTHER need on the same
 * property. A milk run is a second TRIP worth bundling; a second scope at one
 * address is the same trip, and offering it as a milk run is how every
 * dual-scope property came to badge "Milk run · 0 mi".
 *
 * Neither identity is the coordinate pair, deliberately. Coordinate equality
 * cannot separate "one property, two scopes" from "two properties sharing a
 * point", and it would also mask a real order-service defect rather than
 * expose it: a need's coordinate is its PROJECT's centroid
 * (`SalesforceHandler.applyStaffingNeedGeography` sends
 * `project.getCenterGeo()`), so needs on different sites of one multi-site
 * project all carry one point. Those are genuinely different addresses, so
 * they are still measured — and still read 0 mi — which is the honest
 * rendering of a coordinate that is wrong at the source.
 */
export interface AvailableWorkProximityCandidate {
  key: string;
  position: LatLng | null;
  /** Row title, surfaced as the "bundle with this one" label. */
  title: string;
  /**
   * Property identity (`availableWorkPropertyKey`), so a sibling need on the
   * SAME property is not offered as a trip to bundle. `''` means "unknown",
   * and never matches — see `availableWorkProximity`.
   */
  propertyKey: string;
}

/** The proximity facts a milk-run badge, its tooltip, and the sort all read. */
export interface AvailableWorkProximityFacts {
  nearestNeedMiles: number;
  isMilkRun: boolean;
  closestNeedName: string | null;
  closestNeedMiles: number | null;
  /** Distance as the badge renders it — "<1 mi" under half a mile, else "<n> mi"; '' when unknown. */
  closestNeedDistanceLabel: string;
  milkRunTooltip: string;
}

/**
 * Proximity of one need to the NEAREST OTHER need in `referenceSet` — the
 * milk-run semantics CONNECT 1.5 defines ("proximity to another site in the
 * current result set, so the vendor can bundle a trip"), not proximity to the
 * vendor's own work.
 *
 * Measuring against the vendor's own sites is what made a stale job badge a
 * milk run: that feed carries COMPLETED / BID_LOST / DECLINED / NO_RESPONSE /
 * CANCELLED work, so a need 12 miles from a job finished 18 months ago badged,
 * sorted to the top, and entered the Milk Run Only subset. Another OPEN need is
 * by definition current, and it answers for a vendor with no current work at
 * all — who previously got `NaN` on every row.
 *
 * A need on the SAME property is skipped along with the row itself — see
 * `AvailableWorkProximityCandidate`. So a dual-scope property whose only
 * neighbour is its own second scope now measures as having no neighbour at all
 * (`NaN`), not as one 0 miles away.
 *
 * `NaN` when the answer is unknowable: no position, or no other need in the set
 * with a usable one. Non-finite candidates are SKIPPED rather than allowed to
 * win the `<` comparison, and the label is recorded only alongside a finite
 * distance — so a coordinate-less neighbour can never leave a garbage name or a
 * `Math.round(NaN)` behind. Treating an unknown distance as 0 would sort
 * coordinate-less needs to the top and badge them, the opposite of the truth.
 */
export function availableWorkProximity(
  row: AvailableWorkProximityCandidate,
  referenceSet: AvailableWorkProximityCandidate[] | null | undefined
): AvailableWorkProximityFacts {
  let nearest = NaN;
  let name: string | null = null;
  for (const candidate of referenceSet ?? []) {
    if (candidate.key === row.key) {
      continue; // self is never its own neighbour
    }
    // A sibling scope on the SAME property is not a trip to bundle. Guarded on
    // a non-empty key: `''` is "this need carries no address", and treating
    // that as an identity would put every address-less need on one imaginary
    // property and strike them all out of each other's neighbourhood.
    if (row.propertyKey !== '' && candidate.propertyKey === row.propertyKey) {
      continue;
    }
    const miles = haversineMiles(row.position, candidate.position);
    if (Number.isFinite(miles) && (!Number.isFinite(nearest) || miles < nearest)) {
      nearest = miles;
      name = candidate.title;
    }
  }
  return availableWorkProximityFacts(nearest, name);
}

/** The unknowable case — a need outside the reference set, or with nothing to measure to. */
export function unknownAvailableWorkProximity(): AvailableWorkProximityFacts {
  return availableWorkProximityFacts(NaN, null);
}

/** Assemble the derived proximity fields from a (possibly `NaN`) distance + label. */
function availableWorkProximityFacts(
  miles: number,
  name: string | null
): AvailableWorkProximityFacts {
  const known = Number.isFinite(miles);
  const rounded = known ? Math.round(miles) : null;
  const distanceLabel = known ? formatMilkRunMiles(miles) : '';
  return {
    nearestNeedMiles: miles,
    isMilkRun: isMilkRunDistance(miles),
    closestNeedName: known ? name : null,
    closestNeedMiles: rounded,
    closestNeedDistanceLabel: distanceLabel,
    milkRunTooltip: known && name ? `Bundle with ${name} · ${distanceLabel} away` : '',
  };
}

/**
 * Distance as the badge says it — `"<1 mi"` under half a mile, else `"<n> mi"`.
 *
 * `Math.round` alone prints anything under half a mile as `"0 mi"`, which reads
 * as a broken calculation rather than as "very close" — it is the string that
 * made the dual-scope defect visible, and it would still appear for a genuinely
 * sub-mile pair of DIFFERENT properties, which is a real and unremarkable case.
 * `"<1 mi"` says the same fact without looking like a bug. Exact zero is folded
 * in with it deliberately: two distinct properties on one coordinate is a
 * coordinate that is wrong upstream, and claiming "0 mi" would be asserting the
 * one thing that cannot be true of two different addresses.
 */
function formatMilkRunMiles(miles: number): string {
  return miles < 0.5 ? '<1 mi' : `${Math.round(miles)} mi`;
}

/** True only for a FINITE distance within the milk-run threshold — `NaN` is never near. */
function isMilkRunDistance(miles: number): boolean {
  return Number.isFinite(miles) && miles <= MILK_RUN_MILES;
}

/**
 * Proximity comparator — nearest first, unknown-distance rows last.
 *
 * The `NaN` branches are load-bearing: `NaN - x` is `NaN`, and a comparator
 * that ever returns `NaN` makes `Array.prototype.sort` behave as if the pair
 * were equal, which can scramble the rest of the order rather than merely
 * misplacing the unknown row. Equal/both-unknown returns 0, and `sort` is
 * stable, so those rows keep their feed order.
 */
export function compareAvailableWorkProximity(
  a: { nearestNeedMiles: number },
  b: { nearestNeedMiles: number }
): number {
  const aKnown = Number.isFinite(a?.nearestNeedMiles);
  const bKnown = Number.isFinite(b?.nearestNeedMiles);
  if (!aKnown && !bKnown) {
    return 0;
  }
  if (!aKnown) {
    return 1;
  }
  if (!bKnown) {
    return -1;
  }
  return a.nearestNeedMiles - b.nearestNeedMiles;
}

/** Copy of a row set ordered by proximity (nearest first, unknown last). */
export function sortAvailableWorkRowsByProximity<T extends { nearestNeedMiles: number }>(
  rows: T[]
): T[] {
  return [...(rows ?? [])].sort(compareAvailableWorkProximity);
}

// -----------------------------------------------------------------------------
// Available Work filters — THREE axes, deliberately not four:
//   * Report type — a dropdown, seeded from the result set.
//   * All / Needs Staffing / Critical — single-select, driven by `rush` through
//     the shared `workMapRowStatus` helper (NOT a new axis).
//   * Milk run only — an INDEPENDENT boolean, so it composes with the other
//     two; it is not a fourth tab in the status group.
// There is no work-type axis: a need is neither a Quote Request nor a Work
// Assignment, and an axis with one value is noise.
// -----------------------------------------------------------------------------

/** Status tab selection for the Available Work scope. */
export type AvailableWorkStatusFacet = 'all' | 'needsStaffing' | 'critical';

/** The Available Work filter selection — `'all'` sentinels mean "no constraint". */
export interface AvailableWorkFilter {
  reportType: string; // 'all' or a report-type display value
  status: AvailableWorkStatusFacet;
  milkRunOnly: boolean;
}

/**
 * The three axes an Available Work filter reads. A row view satisfies this
 * structurally, so the matcher/counter can be exercised on plain literals
 * without building a full view-model.
 */
export interface AvailableWorkFacetInput {
  reportType: string;
  status: WorkMapStatus;
  isMilkRun: boolean;
}

/** Distinct report-type values present across an Available Work row set, sorted. */
export function availableWorkReportTypeOptions(rows: { reportType: string }[]): string[] {
  const seen = new Set<string>();
  for (const row of rows ?? []) {
    if (row?.reportType) {
      seen.add(row.reportType);
    }
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

/**
 * The report-type axis on its own.
 *
 * Extracted because it has a SECOND caller: the milk-run reference set is the
 * report-type-narrowed set (see `buildAvailableWorkRows`). Both must agree on
 * what "narrowed by report type" means, so neither owns its own copy of the
 * predicate.
 */
export function matchesAvailableWorkReportType(
  input: { reportType: string },
  filter: { reportType: string }
): boolean {
  return filter.reportType === 'all' || input.reportType === filter.reportType;
}

/** True when a row passes all three Available Work axes (they compose, AND-wise). */
export function matchesAvailableWorkFilter(
  input: AvailableWorkFacetInput,
  filter: AvailableWorkFilter
): boolean {
  if (!matchesAvailableWorkReportType(input, filter)) {
    return false;
  }
  if (filter.status !== 'all' && input.status !== filter.status) {
    return false;
  }
  return !filter.milkRunOnly || input.isMilkRun;
}

/** Apply the Available Work filter to a row set, preserving (proximity) order. */
export function filterAvailableWorkRows<T extends AvailableWorkFacetInput>(
  rows: T[],
  filter: AvailableWorkFilter
): T[] {
  return (rows ?? []).filter((row) => matchesAvailableWorkFilter(row, filter));
}

/**
 * Live counts over the FULL (unfiltered) row set — the status subsets partition
 * it, while `milkRun` overlaps both, matching how the controls compose. An
 * empty or failed fetch yields all zeros, which is what keeps the controls
 * rendered (with 0 counts) instead of vanishing.
 *
 * One asymmetry worth naming: `all`/`needsStaffing`/`critical` are genuinely
 * report-type-independent, but `milkRun` is not, because `isMilkRun` is now
 * measured within the report-type-narrowed reference set — a need outside that
 * set carries `NaN` (see `buildAvailableWorkRows`). So with a report type
 * selected, the Milk Run Only count reads as "milk runs among the selected
 * service type", which is exactly the subset the toggle can reach. It is still
 * status-independent, so it keeps overlapping both status subsets.
 */
export function availableWorkFacetCounts(
  inputs: AvailableWorkFacetInput[]
): { all: number; needsStaffing: number; critical: number; milkRun: number } {
  let needsStaffing = 0;
  let critical = 0;
  let milkRun = 0;
  for (const input of inputs ?? []) {
    if (input.status === 'critical') {
      critical++;
    } else {
      needsStaffing++;
    }
    if (input.isMilkRun) {
      milkRun++;
    }
  }
  return { all: (inputs ?? []).length, needsStaffing, critical, milkRun };
}
